import { List } from 'immutable';
import { createSelector } from 'reselect';

import { getCustomerById, getCustomers } from 'app-components/flux/customers/customersSelectors';
import { invoiceItemsSelectors } from 'app-components/flux/invoice-items/invoiceItemsSlice';
import {
  getNumberOfActiveProjects,
  getProjectById,
  getTotalNumberOfProjects,
} from 'app-components/flux/projects/projectsSelectors';
import { getInvoiceMixpanelData } from 'app-components/flux/transactions/invoicesSelectors';
import { getResourcesById as getTransactionsResourcesById } from 'app-components/flux/transactions/transactionsSelectors';
import { TransactionSubType } from 'app-components/flux/transactions/transactionsTypes';
import { createAppSelector } from 'app-components/utils/core/reselect';
import type RootState from 'app-components/utils/redux-store/rootState';

import { type BusinessColor, businessColorSequence } from '../../utils/theme';
import { accountsSelectors } from '../accounts';
import { entitlementsSelectors } from '../entitlements';
import { mileageTrackerSelectors } from '../mileage-tracker/mileageTrackerSlice';
import { MileageTripType } from '../mileage-tracker/mileageTrackerTypes';
import { preferencesV2Selectors } from '../preferences-v2';
import { BUSINESS_REDUCER_KEY, type Business, taxGroupNames } from './businessTypes';

type State = Pick<RootState, typeof BUSINESS_REDUCER_KEY>;
export const getStore = (state: State) => state[BUSINESS_REDUCER_KEY];
export const getIsLoading = (state: State) => getStore(state).isLoading;
export const getLoadPending = (state: State) => getStore(state).loadPending;
export const getError = (state: State) => getStore(state).error;
export const getResourcesById = (state: State) => getStore(state).resourcesById;

export const getBusinessForId = createSelector(
  [getResourcesById, (_state: unknown, id: string) => id],
  (businessesById, id) => businessesById.get(id),
);

// TODO: refactor with indices so it supports current palette preference

export const getBusinessColors = createAppSelector([getResourcesById], (businesses) => {
  const colorSet = new Set<BusinessColor>();
  if (businesses.size >= businessColorSequence.length) {
    return businessColorSequence;
  }
  businesses.forEach((business) => {
    if (business.color) {
      colorSet.add(business.color);
    }
  });
  return businessColorSequence.filter((color) => !colorSet.has(color));
});

export const getBusinessNames = createAppSelector([getResourcesById], (businesses) => {
  const nameObj: Record<string, Business> = {};
  businesses.forEach((business) => {
    if (business.name) {
      nameObj[business.name] = business;
    }
  });
  return nameObj;
});

export const getBusinessList = createAppSelector([getResourcesById], (businesses) => {
  const defaultBusiness: Business[] = [];
  const openBusinesses: Business[] = [];
  const closedBusinesses: Business[] = [];

  businesses.forEach((business) => {
    if (business.isDefault) {
      defaultBusiness.push(business);
    } else if (business.isClosed) {
      closedBusinesses.push(business);
    } else {
      openBusinesses.push(business);
    }
  });

  openBusinesses.sort((a, b) => a.createdAt?.localeCompare(b.createdAt ?? '') ?? 0);

  return List(defaultBusiness.concat(openBusinesses).concat(closedBusinesses));
});

// User is eligible for the business upgrade card: business is enabled, data has loaded, and no businesses exist yet
export const getIsBusinessUpgradeEligible = createAppSelector(
  [entitlementsSelectors.getBusinessEnabled, getLoadPending, getBusinessList],
  (businessEnabled, loadPending, businessList) => Boolean(businessEnabled && !loadPending && businessList.size === 0),
);

export const getOpenBusinesses = createAppSelector([getBusinessList], (businesses) =>
  businesses?.filter((biz) => !biz.isClosed),
);

// User can see the business dashboard cards: business is enabled and at least one business is open
export const getIsBusinessCardsEligible = createAppSelector(
  [entitlementsSelectors.getBusinessEnabled, getOpenBusinesses],
  (businessEnabled, openBusinesses): boolean => Boolean(businessEnabled && (openBusinesses?.size ?? 0) > 0),
);

export const getDefaultBusiness = createAppSelector([getOpenBusinesses], (businesses) => {
  if (!businesses) return null;

  let defaultBusiness = businesses.size === 1 ? businesses.get(0) : null;
  if (!defaultBusiness) {
    defaultBusiness = businesses.find((business) => business.isDefault ?? false);
  }
  if (!defaultBusiness) {
    // OK to sort ISO string date by lexicographical order
    defaultBusiness = businesses.minBy((business) => business.createdAt);
  }

  return defaultBusiness;
});

export const getNonDefaultBusinesses = createAppSelector(
  [getOpenBusinesses, getDefaultBusiness],
  (businesses, defaultBusiness) => {
    if (!businesses || !defaultBusiness) {
      return null;
    }
    return businesses?.filter((business) => business?.id !== defaultBusiness?.id && !business?.isClosed);
  },
);

export const hasMultipleOpenBusinesses = createAppSelector([getOpenBusinesses], (businesses) => businesses?.size > 1);

export const getAvailableBusinessesForStripeAccount = createAppSelector(
  [getOpenBusinesses, accountsSelectors.getStripeAccounts],
  (businesses, accounts) => {
    return businesses.filter((biz) => !accounts?.find(({ businessId }) => businessId === biz.id)).toArray();
  },
);

const getBusinessIndustriesAndTaxGroups = createAppSelector([getOpenBusinesses], (businesses) => {
  const { taxGroups, industriesArray } = businesses.reduce<{ taxGroups: string[]; industriesArray: string[] }>(
    (acc, business) => {
      acc.taxGroups.push(taxGroupNames[business.taxGroup as keyof typeof taxGroupNames] || 'Unknown');
      acc.industriesArray.push(business.industry || 'Unknown');
      return acc;
    },
    { taxGroups: [], industriesArray: [] },
  );
  const current_tax_group = taxGroups.join();
  const industries = industriesArray.join();
  return {
    total_businesses: businesses.size,
    current_tax_group,
    industries,
  };
});

const selectTotalSalesReceiptsForMixpanel = createAppSelector(
  [getTransactionsResourcesById],
  (transactions) =>
    transactions?.reduce(
      (count, txn) => (txn?.subtype === TransactionSubType.SalesReceipt && !txn.isDeleted ? count + 1 : count),
      0,
    ) ?? 0,
);

export const getAccountingTypeForProjectId = createAppSelector(
  [getResourcesById, getProjectById],
  (businessById, project) => {
    const business = businessById.get(project?.businessId ?? '');
    if (!business || !project) {
      return null;
    }
    return business.accountingType;
  },
);

export const getAccountingTypeForCustomerId = createAppSelector(
  [getResourcesById, getCustomerById, getDefaultBusiness],
  (businessById, customer, defaultBusiness) => {
    const business = businessById.get(customer?.businessId ?? '') ?? defaultBusiness;
    if (!business || !customer) {
      return null;
    }
    return business.accountingType;
  },
);

export const getBizAccountBarOrderMixpanel = createAppSelector(
  [getResourcesById, (s) => preferencesV2Selectors.getPreferenceValue(s, 'sAccountBarOrder', {})],
  (businessById, accountBarOrder) => {
    let count = 1;
    const bizIdToMixpanelName = businessById
      // Oldest biz first (or default)
      .sort((a, b) => (a.isDefault ? -1 : (a.createdAt ?? '') < (b.createdAt ?? '') ? -1 : 1))
      // Map to mixpanel names
      .reduce<Record<string, string>>(
        (acc, biz) => {
          if (biz.id && biz.name && biz.createdAt) {
            if (count === 1) {
              acc[biz.id] = 'Primary';
            } else {
              acc[biz.id] = `Business ${count}`;
            }
            count++;
          }
          return acc;
        },
        { PERSONAL: 'Personal' },
      );

    const sorted = businessById
      .reduce<Array<string>>((acc, biz) => {
        if (biz.id && biz.name && biz.createdAt) {
          acc.push(biz.id);
        }
        return acc;
      }, [])
      .concat('PERSONAL')
      .sort((a, b) => {
        // First: Sort by Pref if exists
        if (accountBarOrder) {
          const order = (accountBarOrder[a] ?? Infinity) - (accountBarOrder[b] ?? Infinity);
          if (order) {
            return order;
          }
        }

        // If id is "PERSONAL", it should be last
        if (a === 'PERSONAL') return 1;
        if (b === 'PERSONAL') return -1;

        // otherwise, sort by displayLabel (business-name)
        return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
      });

    return sorted.map((id) => bizIdToMixpanelName[id]);
  },
);
export const selectMileageUserDataForMixpanel = createAppSelector([mileageTrackerSelectors.selectAll], (miles) =>
  miles.reduce(
    (acc, trip) => {
      switch (trip.type) {
        case MileageTripType.MilesOnly:
          acc.total_miles_only++;
          break;
        case MileageTripType.TripDetails:
          acc.total_mileage_trips++;
          break;
      }
      return acc;
    },
    { total_mileage_trips: 0, total_miles_only: 0 },
  ),
);

const getEnabledMixpanelBusinessData = createAppSelector(
  [
    getBusinessIndustriesAndTaxGroups,
    getCustomers,
    getNumberOfActiveProjects,
    getTotalNumberOfProjects,
    invoiceItemsSelectors.selectTotal,
    getBizAccountBarOrderMixpanel,
    getInvoiceMixpanelData,
    selectMileageUserDataForMixpanel,
    accountsSelectors.getOpenAccountsById,
    selectTotalSalesReceiptsForMixpanel,
  ],
  (
    taxGroupAndIndustries,
    customers,
    total_active_projects,
    total_projects,
    total_global_items,
    current_account_group_order,
    invoiceData,
    mileageData,
    openAccountsById,
    totalSalesReceipts,
  ) => ({
    ...taxGroupAndIndustries,
    total_clients: customers.size,
    total_active_projects,
    total_projects,
    total_global_items,
    current_account_group_order,
    ...invoiceData,
    ...mileageData,
    total_sales_receipts: totalSalesReceipts,
    total_personal_accounts: openAccountsById.count((account) => !account.businessId),
    total_business_accounts: openAccountsById.count((account) => !!account.businessId),
  }),
);

export const getMixpanelBusinessData = (state: RootState): ReturnType<typeof getEnabledMixpanelBusinessData> | null =>
  entitlementsSelectors.getBusinessEnabled(state) ? getEnabledMixpanelBusinessData(state) : null;
